import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Lock, Eye, EyeOff, Check, X } from 'lucide-react';
import apiClient from '@/lib/apiClient';

// Password policy constants (must match backend)
const PASSWORD_MIN_LENGTH = 10;
const PASSWORD_SPECIAL_CHARS = "!@#$%^&*()_+-=[]{}|;:,.<>?";

function ChangePasswordPage() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Password requirements validation
  const passwordRequirements = useMemo(() => ({
    minLength: newPassword.length >= PASSWORD_MIN_LENGTH,
    hasUppercase: /[A-Z]/.test(newPassword),
    hasLowercase: /[a-z]/.test(newPassword),
    hasNumber: /[0-9]/.test(newPassword),
    hasSpecial: [...newPassword].some(c => PASSWORD_SPECIAL_CHARS.includes(c)),
  }), [newPassword]);

  const isPasswordValid = Object.values(passwordRequirements).every(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!currentPassword.trim() || !newPassword.trim() || !confirmPassword.trim()) {
      toast.error('Bitte füllen Sie alle Felder aus');
      return;
    }
    
    if (!isPasswordValid) {
      toast.error('Das Passwort erfüllt nicht alle Anforderungen');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Die neuen Passwörter stimmen nicht überein');
      return;
    }
    
    if (currentPassword === newPassword) {
      toast.error('Das neue Passwort muss sich vom aktuellen Passwort unterscheiden');
      return;
    }
    
    setLoading(true);
    try {
      const response = await apiClient.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });
      
      // Update token in localStorage
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
      }
      if (response.data.refresh_token) {
        localStorage.setItem('refresh_token', response.data.refresh_token);
      }
      
      toast.success('Passwort erfolgreich geändert');
      navigate('/admin');
    } catch (error) {
      console.error('Change password error:', error);
      const errorMessage = error.response?.data?.detail || 'Fehler beim Ändern des Passworts. Bitte versuchen Sie es erneut.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: 'radial-gradient(circle at top right, rgba(74, 144, 226, 0.08), transparent 55%), #ffffff'
      }}
    >
      <Card className="w-full max-w-md shadow-lg border-slate-200 bg-white">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center items-center gap-3 mb-2">
            <img
              src="/logo-stadtki.jpg"
              alt="StadtKI Logo"
              className="h-14 w-auto"
            />
            <div className="font-semibold text-lg tracking-wide uppercase" style={{ color: '#0b2f52' }}>
              stadtki
            </div>
          </div>
          <CardTitle className="text-2xl font-semibold" style={{ color: '#0b2f52' }}>
            Passwort ändern
          </CardTitle>
          <CardDescription className="text-slate-600">
            Sie müssen Ihr Passwort ändern, bevor Sie fortfahren können
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current-password" className="text-slate-700">
                Aktuelles Passwort
              </Label>
              <div className="relative">
                <Input
                  id="current-password"
                  type={showCurrentPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  disabled={loading}
                  required
                  className="mt-1 bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 focus-visible:ring-sky-400 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-700 focus:outline-none"
                  tabIndex={-1}
                >
                  {showCurrentPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password" className="text-slate-700">
                Neues Passwort
              </Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Sicheres Passwort eingeben"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={loading}
                  required
                  minLength={PASSWORD_MIN_LENGTH}
                  className="mt-1 bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 focus-visible:ring-sky-400 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-700 focus:outline-none"
                  tabIndex={-1}
                >
                  {showNewPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
              {/* Password requirements checklist */}
              {newPassword.length > 0 && (
                <div className="mt-2 p-3 bg-slate-50 rounded-md border border-slate-200">
                  <p className="text-xs font-medium text-slate-600 mb-2">Passwort-Anforderungen:</p>
                  <ul className="space-y-1 text-xs">
                    <li className={`flex items-center gap-2 ${passwordRequirements.minLength ? 'text-green-600' : 'text-slate-500'}`}>
                      {passwordRequirements.minLength ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                      Mindestens {PASSWORD_MIN_LENGTH} Zeichen
                    </li>
                    <li className={`flex items-center gap-2 ${passwordRequirements.hasUppercase ? 'text-green-600' : 'text-slate-500'}`}>
                      {passwordRequirements.hasUppercase ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                      Mindestens ein Großbuchstabe (A-Z)
                    </li>
                    <li className={`flex items-center gap-2 ${passwordRequirements.hasLowercase ? 'text-green-600' : 'text-slate-500'}`}>
                      {passwordRequirements.hasLowercase ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                      Mindestens ein Kleinbuchstabe (a-z)
                    </li>
                    <li className={`flex items-center gap-2 ${passwordRequirements.hasNumber ? 'text-green-600' : 'text-slate-500'}`}>
                      {passwordRequirements.hasNumber ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                      Mindestens eine Zahl (0-9)
                    </li>
                    <li className={`flex items-center gap-2 ${passwordRequirements.hasSpecial ? 'text-green-600' : 'text-slate-500'}`}>
                      {passwordRequirements.hasSpecial ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                      Mindestens ein Sonderzeichen (!@#$%...)
                    </li>
                  </ul>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password" className="text-slate-700">
                Neues Passwort bestätigen
              </Label>
              <div className="relative">
                <Input
                  id="confirm-password"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                  required
                  className="mt-1 bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 focus-visible:ring-sky-400 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-700 focus:outline-none"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading || !currentPassword.trim() || !isPasswordValid || !confirmPassword.trim() || newPassword !== confirmPassword}
              className="w-full bg-sky-600 hover:bg-sky-500 text-white font-semibold shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Passwort ändern...
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4 mr-2" />
                  Passwort ändern
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default ChangePasswordPage;
